import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { pb } from '../../lib/pb';
import type { FeatureRecord, HistoryRecord } from '../../lib/types';
import { formatDate } from '../../i18n';
import { isEditor, useStore } from '../../store/store';
import { updateFeature } from '../../store/actions';

// Content fields a restore puts back. Status and key follow the workflow, so they are not restored.
const RESTORABLE = [
  'name',
  'description',
  'geometry',
  'properties',
  'designProps',
  'showInGame',
  'tags',
  'color',
  'parent',
] as const;

export function HistoryTab({ feature: f }: { feature: FeatureRecord }) {
  const { t } = useTranslation();
  const user = useStore((s) => s.user);
  const users = useStore((s) => s.users);
  const [rows, setRows] = useState<HistoryRecord[]>([]);

  useEffect(() => {
    let cancelled = false;
    void pb
      .collection('history')
      .getList<HistoryRecord>(1, 100, { filter: pb.filter('recordId = {:r}', { r: f.id }), sort: '-created' })
      .then((res) => !cancelled && setRows(res.items));
    return () => {
      cancelled = true;
    };
    // Refetch whenever the feature changes revision.
  }, [f.id, f.rev]);

  const restore = (h: HistoryRecord) => {
    if (!h.after) return;
    if (!confirm(t('history.confirmRestore', { date: formatDate(h.updated) }))) return;
    const patch: Partial<FeatureRecord> & { restoreOf: string } = { restoreOf: h.id };
    for (const k of RESTORABLE) {
      if (h.after[k] !== undefined) (patch as Record<string, unknown>)[k] = h.after[k];
    }
    void updateFeature(f.id, patch);
  };

  if (rows.length === 0) return <p className="muted">{t('history.empty')}</p>;
  return (
    <ol className="history">
      {rows.map((h, i) => (
        <li key={h.id}>
          <div className="small">
            <span className={`hist-action ${h.action}`}>{t(`history.actions.${h.action}`)}</span>{' '}
            <strong>{users[h.actor]?.name ?? t('history.system')}</strong>{' '}
            <span className="muted">{formatDate(h.updated)}</span>
          </div>
          <Summary h={h} />
          {!!h.changedFields?.length && (
            <div className="small">
              {h.changedFields.map((c) => (
                <span key={c} className="chip">
                  {t(`history.fields.${c}`, { defaultValue: c })}
                </span>
              ))}
            </div>
          )}
          {i > 0 && isEditor(user) && h.after && (
            <button className="link small" onClick={() => restore(h)}>
              {t('history.restore')}
            </button>
          )}
        </li>
      ))}
    </ol>
  );
}

/** Status changes are shown translated; other server summaries (imports, deletes) as written. */
function Summary({ h }: { h: HistoryRecord }) {
  const { t } = useTranslation();
  const from = h.before?.status;
  const to = h.after?.status;
  if (h.changedFields?.includes('status') && from && to) {
    return (
      <div className="muted small">
        {t(`status.${from}`)} → {t(`status.${to}`)}
      </div>
    );
  }
  if (h.action === 'create' || h.action === 'update' || h.action === 'restore') return null;
  return h.summary ? <div className="muted small">{h.summary}</div> : null;
}
